import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import axios from "axios";
import { getToken } from "utils/getToken";

// Webview opened from the doctor-app consult-claim push:
//   support4.gagahealth.com/consultation-claim?appointment_id=<id>
// Auth rides the portal's own login (getToken from localStorage), same as the
// chat webview. Calls picard /consultation/claim/{detail,} endpoints.
const Shell = ({ children }: { children: ReactNode }) => (
  <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-lg">
      {children}
    </div>
  </div>
);

const Row = ({ label, value }: { label: string; value?: ReactNode }) => (
  <p>
    <span className="font-semibold">{label}:</span> {value || "—"}
  </p>
);

const ConsultationClaim = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  // Works for BOTH routes:
  //  - /consultation-claim?appointment_id=<id>&token=<t>  (fixed app / direct)
  //  - /chat/<id>?name=<t>   (current released app: chat_webview hardcodes /chat/<conversation_id>
  //    and forwards user_name as ?name= — so the backend sends conversation_id=<appt>, user_name=<token>)
  const appointmentId = params.appointmentId || searchParams.get("appointment_id");
  // Doctor-app webview: token comes in the URL (?token= or ?name=); the static SPA can't read the
  // app's Authorization header and the doctor isn't logged into the portal. Browser falls back to localStorage.
  const token = searchParams.get("token") || searchParams.get("name") || getToken();

  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [result, setResult] = useState<any>(null); // { status, message, assigned_to? }

  const authHeaders = { headers: { Authorization: `Token ${token}` } };

  useEffect(() => {
    if (!appointmentId) {
      setError("Missing appointment id.");
      setLoading(false);
      return;
    }
    axios
      .get(
        process.env.REACT_APP_API_URL +
          `consultation/claim/detail/?appointment_id=${appointmentId}`,
        authHeaders
      )
      .then((r) => setDetail(r.data))
      .catch(() => setError("Could not load this consultation."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  const accept = async () => {
    setAccepting(true);
    try {
      const r = await axios.post(
        process.env.REACT_APP_API_URL + "consultation/claim/",
        { appointment_id: appointmentId },
        authHeaders
      );
      setResult(r.data);
    } catch (e: any) {
      setResult({
        status: "error",
        message:
          e?.response?.data?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setAccepting(false);
    }
  };

  if (loading)
    return (
      <Shell>
        <p className="text-center text-gray-500">Loading…</p>
      </Shell>
    );

  if (error)
    return (
      <Shell>
        <p className="text-center text-red-500">{error}</p>
      </Shell>
    );

  const assignedElsewhere = detail?.assigned && result?.status !== "assigned";

  return (
    <Shell>
      <h1 className="mb-4 text-xl font-bold text-gray-800">New consultation</h1>

      <div className="space-y-1.5 text-gray-700">
        <Row label="Patient" value={detail?.patient_name} />
        <Row label="Mobile" value={detail?.patient_mobile} />
        <Row label="Type" value={detail?.type} />
        <Row label="Status" value={detail?.status} />
      </div>

      {assignedElsewhere && !result && (
        <p className="mt-3 rounded-lg bg-amber-50 p-2 text-center text-amber-700">
          Already assigned{detail?.assigned_to ? ` to ${detail.assigned_to}` : ""}.
        </p>
      )}

      {result ? (
        <div
          className={`mt-6 rounded-lg p-3 text-center font-medium ${
            result.status === "assigned"
              ? "bg-green-50 text-green-700"
              : "bg-amber-50 text-amber-700"
          }`}
        >
          {result.message}
        </div>
      ) : (
        <button
          onClick={accept}
          disabled={accepting || assignedElsewhere}
          className="mt-6 w-full rounded-xl bg-green-600 py-3 font-semibold text-white transition hover:bg-green-700 disabled:opacity-50"
        >
          {accepting
            ? "Accepting…"
            : assignedElsewhere
            ? "Already assigned"
            : "Accept & call the patient"}
        </button>
      )}
    </Shell>
  );
};

export default ConsultationClaim;
